import { SAMessageType } from "@missionlabs/smartagent-chat-components-lib/dist/ChatMessage/chat-message.types";
import { MiddlewareStore } from "store/createStore";
import { IChatMessage } from "store/messages/messages.state";
import * as Logger from '../../../../services/logger'
import createBchat from "@missionlabs/smartagent-bchat";
import { IMessageQueuerResponse } from "./messageQueuer";
import { handleAttachment } from "./onMessage/handleAttachment";
import { handleParticipantJoined } from "./onMessage/handleParticipantJoined";
import { handleSmartagentMessage } from "./onMessage/handleSmartagentMessage";
import MessagesTypes from "store/messages/messages.types";
import { IMessageMap } from "services/messageMap";


export const isMetaEvent = (message: IChatMessage) => {
    const metaEvents = [
        "application/vnd.amazonaws.connect.event.participant.left",
        "application/vnd.amazonaws.connect.event.participant.joined",
        "application/vnd.amazonaws.connect.event.chat.ended",
        "application/vnd.amazonaws.connect.event.transfer.succeeded",
        "application/vnd.amazonaws.connect.event.transfer.failed"
    ]

    return metaEvents.includes(message.contentType)

}

export const transformChatMessage = async (data: connect.ChatEventData | connect.ChatTranscriptItem, store: MiddlewareStore, session: connect.CustomerChatSession): Promise<IChatMessage> => {
    const language = store.getState().app.language;

    let { content, isJson } = ((content: string): { content: SAMessageType | string, isJson: boolean } => {
        try {
            // check if content is wrapped in quotes and throw an error as this is technically valid JSON but we don't want to parse it as such
            if (/^(['"]).*['"]/.test(content)) throw new Error();
            const parsed = JSON.parse(content?.replace('\n', ''));
            if (typeof parsed === 'number') throw new Error();
            // "all" is added here to catch the case where a SILENT_STATE_UPDATE message is sent from connect but the language does not need to be changed and the app should ignore this. The check below ensures "all" will be ignored and the app won't try to render it as a message.          

            if (Array.isArray(JSON.parse(content))) throw new Error();
            if (content === 'true') throw new Error();
            if (content === 'false') throw new Error();
            return {
                content: parsed[language] || parsed["*"],
                isJson: true
            }
        } catch {
            return {
                content,
                isJson: false
            }
        }
    })(data.Content!);

    const readTimestamp =
        !store.getState().app.connectedToAgent
            ? new Date().toISOString() //@ts-ignore
            : data.MessageMetadata?.Receipts[0]?.ReadTimestamp

    //@ts-ignore
    const deliveredTimestamp = data.MessageMetadata?.Receipts[0]?.DeliveredTimestamp

    // allow custom contentType to be set in JSON
    const contentType: connect.ChatContentType =
        ((typeof content === 'object')
            ? (content as SAMessageType)?.contentType || data.ContentType
            : data.ContentType) as connect.ChatContentType;

    return {
        id: data.Id,
        originalMessage: data.Content,
        displayName: data.ParticipantRole === "SYSTEM" ? "Agent" : data.DisplayName!,
        content,
        contentType,
        role: data.ParticipantRole!,
        sentAt: data.AbsoluteTime,
        sendStatus: "success",
        isJson,
        type: data.Type,
        attachments: data.Attachments,
        deliveredTimestamp,
        readTimestamp
    };
};

export const restoreTranscript = async ({
    session,
    store,
    displayMap,
    isChatRehydrated = false,
}: {
    session: connect.CustomerChatSession
    store: MiddlewareStore
    displayMap: IMessageMap
    isChatRehydrated?: boolean
}) => {
    const rawTranscript = await getChatTranscript(session, isChatRehydrated)
    const processedTranscript: IChatMessage[] = []
    const transcript: IChatMessage[] = []

    let currentReceipt: any

    for (const m of rawTranscript) {
        const message = await transformChatMessage(m, store, session)
            .then((message) => handleAttachment(message, store, session, false))
            .then((message) => handleParticipantJoined(message, store))
            .then((message) => handleSmartagentMessage(message, store))

        processedTranscript.push(message as IChatMessage)
    }

    for (const message of processedTranscript.reverse()) {
        const receipt = {
            DeliveredTimestamp: message.deliveredTimestamp,
            ReadTimestamp: message.readTimestamp,
        }

        if (receipt) {
            currentReceipt = receipt
        }

        if (Array.isArray(message)) {
            for (const m2 of message) {
                if (currentReceipt) {
                    m2.deliveredTimestamp = currentReceipt.DeliveredTimestamp
                    m2.readTimestamp = currentReceipt.ReadTimestamp
                }

                !m2.discarded && transcript.unshift(m2)
            }

            continue
        }

        if (currentReceipt) {
            message.deliveredTimestamp = currentReceipt.DeliveredTimestamp
            message.readTimestamp = currentReceipt.ReadTimestamp
        }

        const displayAs = displayMap.get(message.id)

        if (displayAs) {
            message.content = displayAs
        }

        if (!message.discarded) {
            transcript.unshift(message)
        }
    }

    if (isChatRehydrated) {
        //Remove old messages to prevent rendering duplicates
        store.dispatch({
            type: MessagesTypes.CLEAR_MESSAGES,
        })
    }

    store.dispatch({
        type: MessagesTypes.NEW_MESSAGE,
        payload: transcript,
    })
}

export const getChatTranscript = async (
    session: connect.CustomerChatSession,
    isChatRehydrated: boolean = false,
) => {
    try {
        let transcript: connect.ChatTranscriptItem[] = []
        let nextToken: string | undefined

        do {
            const { data } = await session.getTranscript({
                contactId: isChatRehydrated ? undefined : session.getChatDetails().contactId,
                maxResults: 100,
                sortOrder: 'ASCENDING',
                nextToken,
            })

            if (data) {
                transcript = data.Transcript.concat(transcript)
                nextToken = data.NextToken
            } else {
                nextToken = undefined
            }
        } while (nextToken)

        console.log('got transcript', transcript)

        return transcript
    } catch (e) {
        Logger.error('Unable to get chat transcript', e)
        console.log('error getting transcript for chat ', e)
        return []
    }
}

interface IBchatConnect {
    apiKey: string | undefined,
    websocketUrl: string | undefined
}

export const connectBChat = async ({
    apiKey,
    websocketUrl
}: IBchatConnect) => {

    if (!apiKey || !websocketUrl) throw new Error('missing apiKey or websocketUrl');

    console.log('connecting')
    try {
        return window.bchat = await createBchat({
            connectionTimeoutMs: 7000,
            wsUrl:
                websocketUrl +
                (apiKey
                    ? '?Authorization=' + apiKey
                    : '')

        });
    }
    catch (err) {
        const failedMessage = `failed to connect ${err}`
        Logger.error(failedMessage,);
        throw new Error(failedMessage);
    }

}

export const syncMessages = async (session: connect.CustomerChatSession, store: MiddlewareStore, messageQueue: IMessageQueuerResponse) => {
    const { contactId, initialContactId, participantId, participantToken, connectionDetails } = session.getChatDetails();
    const transcript = (await getChatTranscript(session))?.filter(x => x.ParticipantRole !== 'CUSTOMER');
    const messages = store.getState().messages;
    transcript.forEach((message) => {
        const { Id } = message;
        if (messages.find(({ id: messageId }) => messageId === Id) === undefined) {
            const newMessage = {
                chatDetails: {
                    contactId,
                    initialContactId,
                    participantId,
                    participantToken,
                    connectionDetails
                },
                data: message

            } as connect.ChatMessageEvent
            messageQueue.queue(newMessage)
        }
    });
}